import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { fetchBhriguQuota } from '../lib/askBhriguApi';

/** Daily AskGuru caps once the free trial is used up. */
export const ASK_GURU_DAILY_LIMITS = {
  global: 30,
  page_chat: 20,
};

/** @typedef {{ freeUsed: number; freeLimit: number; freeRemaining: number; globalAiToday: number; pageAiToday: number; isAdmin?: boolean }} AskGuruQuota */

/**
 * Daily AskGuru allowance remaining for a surface, after the free trial is spent.
 * @param {AskGuruQuota | null} quota
 * @param {'global' | 'page_chat'} surface
 */
export function getAskGuruDailyRemaining(quota, surface) {
  const limit = ASK_GURU_DAILY_LIMITS[surface] ?? ASK_GURU_DAILY_LIMITS.global;
  const used = surface === 'page_chat' ? quota?.pageAiToday ?? 0 : quota?.globalAiToday ?? 0;
  return Math.max(0, limit - used);
}

export function useAskGuruQuota() {
  const { user } = useAuth();
  const [quota, setQuota] = useState(/** @type {AskGuruQuota | null} */ (null));
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setQuota(null);
      return null;
    }
    setLoading(true);
    try {
      const q = await fetchBhriguQuota();
      setQuota(q);
      return q;
    } catch {
      setQuota(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { quota, loading, refresh };
}
